// src/layouts/MarketPlaceLayout.jsx

import React, { useState, useEffect } from "react";
import { Layout, Badge, Drawer, Checkbox, Typography, Spin, Divider , Grid } from "antd";
import {
  FilterOutlined,
  ArrowLeftOutlined,
  MenuOutlined,
  ContactsOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../components/LoggedOut/HeaderLogic/HeaderBar";
import { getAllBrands } from "../services/brandsServices";
import FiltersPanel from "../components/LoggedOut/FilterLogic/FiltersPanel";
import MobileMenuDrawer from "../components/LoggedOut/DrawerLogic/MobileMenuDrawer"
import FavoritesDrawer from "../components/LoggedOut/DrawerLogic/FavoritesDrawer"
import MessagesDrawer from "../components/LoggedOut/DrawerLogic/MessagesDrawer";
import useBrandsOptions from "../components/LoggedOut/hooks/useBrandsOptions";
import "../styles/MarketPlaceLayout.css";

const { Header, Content } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

export default function MarketPlaceLayout({
  children,
  favorites = [],
  onRemoveFavorite = () => {},
  onFilterSubmit = () => {},
  onClearFilters = () => {},
}) {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  // Estados para Drawers
  const [menuDrawerVisible, setMenuDrawerVisible] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [showFavoritesDrawer, setShowFavoritesDrawer] = useState(false);
  const [showMessagesDrawer, setShowMessagesDrawer] = useState(false);

  // Estados de búsqueda y filtros
  const [searchText, setSearchText] = useState("");
  const [priceRange, setPriceRange] = useState([0, 30000]);
  const [selectedBrands, setSelectedBrands] = useState([]);

  // Opciones de marcas para el Checkbox
  const [brandsOptions, setBrandsOptions] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingBrands(true);
        const allBrands = await getAllBrands();
        if (!mounted) return;
        const options = (allBrands || []).map((m) => ({
          label: m.FCNombre,
          value: m.FCNombre,
        }));
        setBrandsOptions(options);
      } catch (err) {
        console.error("Error al cargar marcas:", err);
        if (mounted) setBrandsOptions([]);
      } finally {
        if (mounted) setLoadingBrands(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // Handlers del header
  const toggleMenuDrawer = () => {
    setMenuDrawerVisible((prev) => !prev);
  };
  const toggleFilterDrawer = () => {
    setFilterDrawerVisible((prev) => !prev);
  };
  const openFavoritesDrawer = () => {
    setShowFavoritesDrawer(true);
  };
  const closeFavoritesDrawer = () => {
    setShowFavoritesDrawer(false);
  };
  const openMessagesDrawer = () => {
    setShowMessagesDrawer(true);
  };
  const closeMessagesDrawer = () => {
    setShowMessagesDrawer(false);
  };
  const handleContactos = () => {
    navigate("/contacto");
  };
  const handleLogin = () => {
    navigate("/login");
  };
  const handleSearch = () => {
    onFilterSubmit({
      searchText: searchText.trim(),
      priceRange,
      selectedBrands,
    });
  };

  // Handlers de filtros
  const handlePriceChange = (newRange) => {
    setPriceRange(newRange);
  };
  const handleBrandsChange = (checkedValues) => {
    setSelectedBrands(checkedValues);
  };
  const handleApplyFilters = () => {
    onFilterSubmit({
      searchText: searchText.trim(),
      priceRange,
      selectedBrands,
    });
    setFilterDrawerVisible(false);
  };
  const handleClearFilters = () => {
    setPriceRange([0, 30000]);
    setSelectedBrands([]);
    onClearFilters();
  };

  return (
    <Layout className="mpl-layout">
      {/* ========== HEADER EXTERNO ========== */}
      <Header>
        <HeaderBar
          isMobile={isMobile}
          searchText={searchText}
          onSearchTextChange={setSearchText}
          handleSearch={handleSearch}
          favoritesCount={favorites.length}
          openFavoritesDrawer={openFavoritesDrawer}
          handleLogin={handleLogin}
          handleContactos={handleContactos}
          toggleFilterDrawer={toggleFilterDrawer}
          toggleMenuDrawer={toggleMenuDrawer}
        />
      </Header>

      {/* ========== DRAWER DE MENÚ (solo móvil) ========== */}
      {isMobile && (
        <MobileMenuDrawer
             visible={menuDrawerVisible}
             onClose={toggleMenuDrawer}
             onContactClick={handleContactos}
             onFilterClick={toggleFilterDrawer}
           />
      )}

      {/* ========== DRAWER DE FILTROS (con FiltersPanel) ========== */}
      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined /> <span>Filtros</span>
          </div>
        }
        placement="left"
        onClose={toggleFilterDrawer}
        open={filterDrawerVisible}
        styles={{ body: { padding: 16, backgroundColor: "#ffffff" } }}
        width={320}
        closeIcon={<ArrowLeftOutlined style={{ color: "#000", fontSize: 20 }} />}
      >
        <FiltersPanel
          priceRange={priceRange}
          onPriceChange={handlePriceChange}
          selectedBrands={selectedBrands}
          onBrandsChange={handleBrandsChange}
          loadingBrands={loadingBrands}
          brandsOptions={brandsOptions}
          onOpenMessages={openMessagesDrawer}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
        />
      </Drawer>

      {/* ========== DRAWER DE FAVORITOS ========== */}
      <FavoritesDrawer
        visible={showFavoritesDrawer}
        onClose={closeFavoritesDrawer}
        favorites={favorites}
        onRemoveFavorite={onRemoveFavorite} 
      />

      {/* ========== DRAWER DE MENSAJES (versión original) ========== */}
      <MessagesDrawer
         visible={showMessagesDrawer}
         onClose={closeMessagesDrawer}
       />

      {/* ========== CONTENT PRINCIPAL ========== */}
      <Content className="mpl-content">{children}</Content>
    </Layout>
  );
}
